import { Link, useLocation } from 'react-router-dom'
import {
  IconDashboard,
  IconImport,
  IconInbox,
  IconMenu,
  IconMoney,
} from '@/components/Icon'
import { podeAcessar } from '@/lib/permissoes'
import { useSessao } from '@/lib/SessaoProvider'
import { cn } from '@/lib/cn'

/**
 * Barra inferior — navegação no celular.
 *
 * No celular o menu lateral exige dois toques (abrir a gaveta, escolher o item)
 * para toda troca de tela. A barra inferior resolve os destinos frequentes em um
 * toque, e fica no alcance do polegar.
 *
 * **Os 5 itens são os mais usados no dia a dia**, não uma amostra do menu:
 * Dashboard para a visão geral, Importação porque é o módulo principal, DUIMP e
 * Financeiro pelo volume de uso, e Menu para o resto.
 *
 * Item sem permissão é **omitido**, não desabilitado — botão que não funciona
 * ocupa espaço escasso sem servir para nada.
 */

const ITENS = [
  { to: '/dashboard', label: 'Início', icone: IconDashboard },
  { to: '/importacao', label: 'Importação', icone: IconImport },
  { to: '/duimp', label: 'DUIMP', icone: IconInbox },
  { to: '/financeiro', label: 'Financeiro', icone: IconMoney },
]

export function BarraInferior({ onAbrirMenu }: { onAbrirMenu: () => void }) {
  const { pathname } = useLocation()
  // `useSessao` devolve a sessão direto, não um objeto com `usuario`.
  const sessao = useSessao()

  const visiveis = ITENS.filter((i) => podeAcessar(sessao, i.to))

  return (
    <nav
      aria-label="Navegação principal"
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 flex border-t border-ink-200 bg-white lg:hidden',
        // Respeita a área segura do aparelho (barra de gestos do iPhone).
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {visiveis.map((i) => {
        const Icone = i.icone
        // `/duimp/custos` também acende o item DUIMP.
        const ativo = pathname === i.to || pathname.startsWith(`${i.to}/`)
        return (
          <Link
            key={i.to}
            to={i.to}
            aria-current={ativo ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center gap-0.5 py-2 text-2xs transition-colors',
              ativo ? 'font-medium text-brand-700' : 'text-ink-500',
            )}
          >
            <Icone className={cn('size-5', ativo && 'text-brand-600')} />
            {i.label}
          </Link>
        )
      })}

      <button
        type="button"
        onClick={onAbrirMenu}
        aria-label="Abrir menu completo"
        className="flex flex-1 flex-col items-center gap-0.5 py-2 text-2xs text-ink-500"
      >
        <IconMenu className="size-5" />
        Menu
      </button>
    </nav>
  )
}
